import React, { useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { BottomSheetPanel } from "../../../components/CustomModel/BottomSheetPanel";
import i18n, { t } from "i18next";
import useAxios from "../../../utils/useAxios";
import {
    getOrdinalSuffix,
    getRoundTitle,
    groupCharCode,
    buildProfileAction
} from "../../../constants";
import { renderParticipantRow } from "../../../components/renderParticipantRow";
import MatchParticipantAvatars from "../../../components/MatchParticipantAvatars";
import ParticipantActionsModal from "../../../components/ParticipantActionsModal";
import { MatchScoreColumns, SetSwitcher } from "../matches/MatchScoreColumns";
import {MatchStatusSelect} from "../matches/MatchStatusSelect";
import {useMatchSync, getNormalizedSets} from "../../../hooks/useMatchSync";
import useTournamentPlayerImages from "../../../hooks/useTournamentPlayerImages";
import { chipsMatchEntity, searchStateClass } from "../../../utils/searchMatch";

// side is 1 or 2 (matches match.player_1/player_2's own numbering) - grade 2
// pairs it with the _3/_4 doubles partner slot. Team matches (grade 3) never
// get avatars at all (a photo pair doesn't represent a team the way it does
// an individual/pair), so this returns null before even touching `images`.
const getAvatarData = (match, side, grade, images) => {
    if (grade === 3) return null;
    const slots = grade === 2
        ? (side === 1 ? [[match.player_1, match.player_1_name], [match.player_3, match.player_3_name]] : [[match.player_2, match.player_2_name], [match.player_4, match.player_4_name]])
        : (side === 1 ? [[match.player_1, match.player_1_name]] : [[match.player_2, match.player_2_name]]);
    const present = slots.filter(([id]) => id);
    if (!present.length) return null;
    return {
        images: present.map(([id]) => images[id]),
        names: present.map(([, name]) => name ? `${name.first_name || ""} ${name.last_name || ""}`.trim() : "")
    };
};

const hasParticipant = (match, side, grade) => {
    if (!match) return false;
    if (grade === 3) return side === 1 ? !!(match.team_1_name || match.team_1_number) : !!(match.team_2_name || match.team_2_number);
    if (grade === 2) return side === 1 ? !!(match.player_1_name || match.player_3_name) : !!(match.player_2_name || match.player_4_name);
    return side === 1 ? !!match.player_1_name : !!match.player_2_name;
};

const TournamentScheduleMatchesModal = ({
                                            isOpen,
                                            onClose,
                                            interactive,
                                            rounds = [],
                                            courts = [],
                                            tournamentId,
                                            tournamentGrade,
                                            fetchSchedule,
                                            selectedMatchId,
                                            setSelectedMatchId,
                                            expandSignal,
                                            stages,
                                            activeStage,
                                            is_authorized,
                                            updateMatches,
                                            searchChips = [],
                                            teamRoster
                                        }) => {
    const api = useAxios();
    const history = useHistory();
    const location = useLocation();
    const [scores, setScores] = useState({});
    const [loadingMatches, setLoadingMatches] = useState({});
    // Which set (0-4) each match's score inputs/SetSwitcher currently show -
    // keyed by match id, same reasoning as TournamentGroupsMatchesModal.js's
    // own activeSetIndexes.
    const [activeSetIndexes, setActiveSetIndexes] = useState({});
    const [teamMenu, setTeamMenu] = useState(null);
    const playerImages = useTournamentPlayerImages(api, tournamentId, tournamentGrade !== 3);

    // Same pattern as TournamentGridMatchesModal's own handlePlayerClick -
    // replace (not push) so the tournament header's back-arrow goBack()
    // doesn't just undo this instead of leaving the tournament.
    const handlePlayerClick = (pId) => {
        const params = new URLSearchParams(location.search);
        params.set("player_id", pId);
        history.replace({ search: params.toString() });
    };

    // "Who's on this team" menu - mirrors Bracket.js/BracketClassic.js's own
    // openParticipantMenu, but reads the already-prefetched teamRoster map
    // (see fetchTeamRosterMap.js) instead of fetching on demand.
    const openTeamMenu = (teamName) => {
        const roster = teamRoster?.get(teamName) || [];
        setTeamMenu({ title: teamName, actions: roster.map(p => buildProfileAction(p, history, location)) });
    };

    const syncMatchApi = useMatchSync(tournamentId, (res) => {
        if (res?.data?.updated_matches) {
            if (updateMatches) updateMatches(res.data.updated_matches);
            setLoadingMatches(prev => {
                const next = { ...prev };
                res.data.updated_matches.forEach(m => delete next[m.id]);
                return next;
            });
        }
    });

    const saveMatch = async (match, newFields = {}, setIndex = 0) => {
        // ЗЕРКАЛИМ БЭКЕНД: Защита от прыжков статуса
        if (newFields.winner_side !== undefined && newFields.status === undefined) {
            const currentStatus = match?.status;
            if (currentStatus !== 5 && currentStatus !== 6 && currentStatus !== 7) {
                newFields.status = 7;
            }
        }

        if (newFields.status !== undefined) {
            setLoadingMatches(prev => ({ ...prev, [match.id]: true }));
        }

        let isSetUpdate = false;
        const mainFields = {};
        const setFields = {};

        const setFieldNames = ['game_score_1', 'game_score_2', 'tiebreak_score_1', 'tiebreak_score_2', 'point_score_1', 'point_score_2', 'serving_player'];

        Object.keys(newFields).forEach(k => {
            if (setFieldNames.includes(k)) {
                setFields[k] = newFields[k];
                isSetUpdate = true;
            } else {
                mainFields[k] = newFields[k];
            }
        });

        const currentSets = getNormalizedSets(match?.sets);
        const localSets = scores[match.id]?.sets ? getNormalizedSets(scores[match.id].sets) : currentSets;
        const newSets = [...localSets];
        for (let i = 0; i <= setIndex; i++) {
            if (!newSets[i]) newSets[i] = { number: i + 1 };
        }

        if (isSetUpdate) {
            newSets[setIndex] = { ...newSets[setIndex], ...setFields, number: setIndex + 1 };
        }

        // Мгновенный UI для админа
        const updated = { ...scores[match.id], ...mainFields };
        if (isSetUpdate) {
            updated.sets = newSets;
        }
        setScores(prev => ({ ...prev, [match.id]: updated }));
        
        if (updateMatches) {
            updateMatches([{ id: match.id, ...mainFields, sets: newSets }], false);
        }

        let payloadToSync;
        if (isSetUpdate) {
            payloadToSync = { sets: [{ ...setFields, number: setIndex + 1 }] };
        } else {
            payloadToSync = { ...newFields };
        }
        syncMatchApi(match.id, payloadToSync);
    };

    // Latest start_time first (same "sit at the top, no scrolling to find
    // the newest" reasoning as TournamentGridMatchesModal's own reversed
    // round order) - court order within the same time slot is just a
    // stable display order for parallel matches, not a "latest first"
    // concept, so that tiebreak stays ascending.
    const scheduledMatches = rounds.filter(r => r.match != null).sort((a, b) => {
        if (a.start_time !== b.start_time) return (b.start_time || "").localeCompare(a.start_time || "");
        return a.court - b.court;
    });

    // true/false once a chip is active, null while there are none - same
    // contract as ScheduleTable.js's own isMatchHighlighted (this sheet
    // lists the exact same matches, just as cards instead of a grid), plus
    // stage/group/draw chip support.
    const matchOf = (match) => {
        if (!match) return null;
        const names = [1, 2, 3, 4].map(num => match[`player_${num}_name`])
            .concat([match.team_1_name, match.team_2_name])
            .filter(Boolean);
        return chipsMatchEntity(searchChips, {
            names,
            stageValue: match.stage_name,
            groupValue: match.group_number,
            drawValue: match.draw_number
        });
    };

    const getCourtName = (courtId) => {
        const court = courts.find(c => c.id === courtId);
        return court ? `${t("Court")} ${court.number || ""}` : "";
    };

    return (
        <BottomSheetPanel isOpen={isOpen} onClose={onClose} interactive={interactive} expandSignal={expandSignal} scrollTargetId={selectedMatchId != null ? `match-${selectedMatchId}` : null} title={t("Matches")} icon="list">
            <form className="modal-form">
                <div className="team-table matches" style={{ marginBottom: "10px" }}>
                    {scheduledMatches.map((roundItem, index) => {
                        const match = roundItem.match;
                        const prevItem = index > 0 ? scheduledMatches[index - 1] : null;
                        const isNewRound = !prevItem || prevItem.order !== roundItem.order;
                        const matched = matchOf(match);
                        const canEdit = is_authorized && hasParticipant(match, 1, tournamentGrade) && hasParticipant(match, 2, tournamentGrade);

                        const matchSets = scores[match?.id]?.sets ? getNormalizedSets(scores[match?.id]?.sets) : getNormalizedSets(match?.sets);

                        const optimisticVals = { ...matchSets[0], winner_side: match.winner_side, ...scores[match.id] };

                        const rows = [
                            { side: 1, name: tournamentGrade === 3
                                    ? renderParticipantRow(match.team_1_name || (match.team_1_number && `${t("Team")} ${match.team_1_number}`), null, match, 0, tournamentGrade, t, true, null, null, openTeamMenu)
                                    : tournamentGrade === 2 ? renderParticipantRow(match.player_1_name, match.player_3_name, match, 0, tournamentGrade, t, true, [match.player_1, match.player_3], handlePlayerClick) : renderParticipantRow(match.player_1_name, null, match, 0, tournamentGrade, t, true, [match.player_1], handlePlayerClick),
                                avatars: getAvatarData(match, 1, tournamentGrade, playerImages),
                                games: optimisticVals.game_score_1, tb: optimisticVals.tiebreak_score_1
                            },
                            { side: 2, name: tournamentGrade === 3
                                    ? renderParticipantRow(match.team_2_name || (match.team_2_number && `${t("Team")} ${match.team_2_number}`), null, match, 1, tournamentGrade, t, true, null, null, openTeamMenu)
                                    : tournamentGrade === 2 ? renderParticipantRow(match.player_2_name, match.player_4_name, match, 1, tournamentGrade, t, true, [match.player_2, match.player_4], handlePlayerClick) : renderParticipantRow(match.player_2_name, null, match, 1, tournamentGrade, t, true, [match.player_2], handlePlayerClick),
                                avatars: getAvatarData(match, 2, tournamentGrade, playerImages),
                                games: optimisticVals.game_score_2, tb: optimisticVals.tiebreak_score_2
                            }
                        ];

                        const activeSetIndex = activeSetIndexes[match.id] || 0;
                        const setActiveSetIndex = (updater) => setActiveSetIndexes(prev => ({
                            ...prev,
                            [match.id]: typeof updater === "function" ? updater(prev[match.id] || 0) : updater
                        }));

                        return (
                            <React.Fragment key={match.id}>
                                {isNewRound && <label style={{marginTop: 0}}>{t("Round")} {roundItem.order}</label>}
                                <div id={`match-${match.id}`} key={match.id} onClick={() => setSelectedMatchId(match.id)} className={`team-card ${String(match.id) === String(selectedMatchId) ? "selected" : (canEdit ? "editable" : "")} ${searchStateClass(matched)}`} style={{ marginBottom: "0" }}>
                                    <div className="team-name-row" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
                                        {match.group_number
                                            ? <>{t("Group")} {groupCharCode(match.group_number)}</>
                                            : match.round_number ? <>{match.draw_number}{getOrdinalSuffix(match.draw_number)} {t("Place")} · {getRoundTitle(match.round_number, match.total, t, i18n, match.match_order)}</>
                                            : match.stage_name ? match.stage_name : ""}
                                        {match.match_number && <> · #{match.match_number}</>}
                                        </span>
                                        {is_authorized && <SetSwitcher activeSetIndex={activeSetIndex} setActiveSetIndex={setActiveSetIndex} />}
                                    </div>

                                    <div>
                                        {rows.map((row, rowIndex) => (
                                            <div key={rowIndex} className="team-player-row" >
                                                <MatchParticipantAvatars images={row.avatars?.images} names={row.avatars?.names} grade={tournamentGrade} />
                                                <div className={`player-name ${optimisticVals.winner_side ? (row.side === optimisticVals.winner_side ? "match-winner" : "match-loser") : ""}`}>{row.name}</div>
                                            </div>
                                        ))}
                                    </div>

                                    <MatchScoreColumns
                                        matchId={match.id}
                                        winnerSide={optimisticVals.winner_side}
                                        rows={rows}
                                        canEdit={canEdit}
                                        is_authorized={is_authorized}
                                        matchSets={matchSets}
                                        activeSetIndex={activeSetIndex}
                                        onSave={(newFields, setIndex) => saveMatch(match, newFields, setIndex)}
                                    />

                                    <div className="team-name-row" style={{position: "relative", display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "10px", marginBottom: "0"}}>
                                        {roundItem.start_time && <span>{roundItem.start_time} · {getCourtName(roundItem.court)}</span>}
                                        {match && <MatchStatusSelect
                                            match={match}
                                            is_authorized={is_authorized}
                                            onSave={(payload) => saveMatch(match, payload)}
                                            min_height={true}
                                            loader={loadingMatches[match.id]}
                                        />}
                                    </div>
                                </div>
                            </React.Fragment>
                        );
                    })}
                </div>
            </form>
            <ParticipantActionsModal
                isOpen={!!teamMenu}
                onClose={() => setTeamMenu(null)}
                title={teamMenu?.title}
                actions={teamMenu?.actions}
            />
        </BottomSheetPanel>
    );
};

export default TournamentScheduleMatchesModal;
